import React from "react";
import styled, { css } from "styled-components";

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: var(--space-2) 0;
`;

const Label = styled.label<{ $error?: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: ${(p) =>
    p.$error ? "var(--color-danger)" : "var(--color-text-secondary)"};
`;

const fieldShellStyles = css<{ $error?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  background: ${(p) =>
    p.$disabled ? "var(--color-surface-subtle)" : "var(--color-surface)"};
  border: 1px solid
    ${(p) => (p.$error ? "var(--color-danger)" : "var(--color-border)")};
  border-radius: var(--radius-md);
  padding: 0 var(--space-3);
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard),
    background var(--duration-fast) var(--ease-standard);

  ${(p) =>
    p.$disabled &&
    css`
      opacity: 0.7;
      cursor: not-allowed;
    `}

  &:focus-within {
    border-color: ${(p) =>
      p.$error ? "var(--color-danger)" : "var(--color-accent)"};
    box-shadow: var(--shadow-focus);
  }
`;

const InputShell = styled.div<{ $error?: boolean; $disabled?: boolean }>`
  ${fieldShellStyles}
  height: 40px;
`;

const TextareaShell = styled.div<{ $error?: boolean; $disabled?: boolean }>`
  ${fieldShellStyles}
  padding: var(--space-3);
  align-items: flex-start;
`;

const NativeInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  color: var(--color-text);

  &::placeholder {
    color: var(--color-text-tertiary);
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--color-text-tertiary);
  }
`;

const Adornment = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  flex-shrink: 0;
  gap: var(--space-1);
`;

const NativeTextarea = styled.textarea`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  line-height: var(--line-height-normal);
  color: var(--color-text);
  resize: vertical;
  min-height: 96px;
  max-height: 240px;

  &::placeholder {
    color: var(--color-text-tertiary);
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--color-text-tertiary);
  }
`;

const ErrorText = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-danger);
`;

type CustomInputProps = {
  label?: string;
  error?: string;
  type?: string;
  value?: any;
  onChange?: any;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
  id?: string;
  rows?: number;
  rowsMax?: number;
  multiline?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  [k: string]: any;
};

const CustomInput = (props: CustomInputProps): any => {
  const {
    error,
    label,
    type,
    disabled,
    id,
    name,
    value,
    onChange,
    placeholder,
    rows,
    rowsMax,
    multiline,
    startAdornment,
    endAdornment,
    ...rest
  } = props;

  const isTextarea = type === "textarea" || multiline;

  return (
    <Wrapper>
      {label && (
        <Label htmlFor={id} $error={Boolean(error)}>
          {label}
        </Label>
      )}
      {isTextarea ? (
        <TextareaShell $error={Boolean(error)} $disabled={Boolean(disabled)}>
          <NativeTextarea
            id={id}
            name={name}
            disabled={disabled}
            value={value ?? ""}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows ?? 5}
            {...rest}
          />
        </TextareaShell>
      ) : (
        <InputShell $error={Boolean(error)} $disabled={Boolean(disabled)}>
          {startAdornment && <Adornment>{startAdornment}</Adornment>}
          <NativeInput
            type={type || "text"}
            id={id}
            name={name}
            disabled={disabled}
            value={value ?? ""}
            onChange={onChange}
            placeholder={placeholder}
            {...rest}
          />
          {endAdornment && <Adornment>{endAdornment}</Adornment>}
        </InputShell>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  );
};

export default CustomInput;
